import { CreditCard, AlertTriangle, Clock, Receipt } from 'lucide-react';
import { Card, Button, ErrorMessage, EmptyState } from '@/components/ui';
import { useSubscriptionStatus, usePaymentHistory, useCreatePayment } from '@/hooks/useBilling';
import type { SubscriptionStatusResponse, PaymentHistoryItem } from '@/types/billing';
import {
  SubscriptionStatus,
  SubscriptionStatusLabels,
  SubscriptionStatusColors,
  PaymentStatusLabels,
  PaymentStatus,
} from '@/types/billing';
import { cn } from '@/lib/utils';

// ── Helpers ────────────────────────────────────────────────

const formatRubles = (kopecks: number) =>
  new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 2,
  }).format(kopecks / 100);

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

const formatDateTime = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const paymentStatusColors: Record<PaymentStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  SUCCEEDED: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
  REFUNDED: 'bg-gray-100 text-gray-700',
};

// ── Subscription Card ──────────────────────────────────────

const SubscriptionCard = ({ sub }: { sub: SubscriptionStatusResponse }) => {
  const isUrgent =
    sub.status === SubscriptionStatus.PAST_DUE ||
    sub.status === SubscriptionStatus.EXPIRED ||
    sub.days_remaining <= 7;

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'w-10 h-10 rounded-xl flex items-center justify-center',
                sub.status === SubscriptionStatus.ACTIVE || sub.status === SubscriptionStatus.TRIALING
                  ? 'bg-emerald-100'
                  : sub.status === SubscriptionStatus.PAST_DUE
                    ? 'bg-yellow-100'
                    : 'bg-red-100',
              )}
            >
              <CreditCard
                className={cn(
                  'w-5 h-5',
                  sub.status === SubscriptionStatus.ACTIVE || sub.status === SubscriptionStatus.TRIALING
                    ? 'text-emerald-600'
                    : sub.status === SubscriptionStatus.PAST_DUE
                      ? 'text-yellow-600'
                      : 'text-red-600',
                )}
              />
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">Подписка</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <span
                  className={cn(
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                    SubscriptionStatusColors[sub.status],
                  )}
                >
                  <span
                    className={cn(
                      'w-1.5 h-1.5 rounded-full',
                      sub.status === SubscriptionStatus.ACTIVE
                        ? 'bg-green-500'
                        : sub.status === SubscriptionStatus.TRIALING
                          ? 'bg-blue-500'
                          : sub.status === SubscriptionStatus.PAST_DUE
                            ? 'bg-yellow-500'
                            : sub.status === SubscriptionStatus.EXPIRED
                              ? 'bg-red-500'
                              : 'bg-gray-400',
                    )}
                  />
                  {SubscriptionStatusLabels[sub.status]}
                </span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-gray-900">{formatRubles(sub.plan_price_kopecks)}</p>
            <p className="text-xs text-gray-500">/мес</p>
          </div>
        </div>
      </Card.Header>

      <Card.Body>
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500">Тариф:</span>
              <span className="font-medium text-gray-900">{sub.plan_name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-500">Период:</span>
              <span className="text-gray-700">
                {formatDate(sub.current_period_start)} — {formatDate(sub.current_period_end)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-gray-500">Осталось дней:</span>
              <span
                className={cn(
                  'font-medium',
                  sub.days_remaining <= 7 ? 'text-red-600' : 'text-gray-900',
                )}
              >
                {sub.days_remaining}
              </span>
            </div>
          </div>

          {sub.is_trial && sub.trial_end && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-100">
              <Clock className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">Пробный период</p>
                <p className="text-sm text-blue-700 mt-0.5">
                  Действует до {formatDate(sub.trial_end)}
                </p>
              </div>
            </div>
          )}

          {sub.is_grace && sub.status === SubscriptionStatus.PAST_DUE && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-yellow-50 border border-yellow-100">
              <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-900">Подписка просрочена</p>
                <p className="text-sm text-yellow-700 mt-0.5">
                  Доступ будет заблокирован в ближайшее время. Оплатите подписку, чтобы продолжить
                  работу.
                </p>
              </div>
            </div>
          )}

          {sub.status === SubscriptionStatus.EXPIRED && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100">
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Подписка истекла</p>
                <p className="text-sm text-red-700 mt-0.5">
                  Оплатите подписку для восстановления доступа.
                </p>
              </div>
            </div>
          )}

          <PayButton subscription={sub} isUrgent={isUrgent} />
        </div>
      </Card.Body>
    </Card>
  );
};

// ── Pay Button ─────────────────────────────────────────────

const PayButton = ({
  subscription,
  isUrgent,
}: {
  subscription: SubscriptionStatusResponse;
  isUrgent: boolean;
}) => {
  const { mutate: createPayment, isPending } = useCreatePayment();

  const handlePay = () => {
    createPayment(window.location.href, {
      onSuccess: (data) => {
        window.location.href = data.confirmation_url;
      },
    });
  };

  return (
    <div className="pt-2">
      <Button
        variant={isUrgent ? 'primary' : 'outline'}
        size="lg"
        onClick={handlePay}
        loading={isPending}
        disabled={isPending || subscription.status === SubscriptionStatus.CANCELED}
        className="w-full"
      >
        <CreditCard className="w-4 h-4" />
        Оплатить подписку
      </Button>
    </div>
  );
};

// ── Payment History ────────────────────────────────────────

const PaymentHistoryTable = ({
  payments,
}: {
  payments: PaymentHistoryItem[];
}) => {
  if (payments.length === 0) {
    return (
      <EmptyState
        icon={<Receipt className="w-8 h-8" />}
        title="Платежей пока нет"
        description="Здесь будет отображаться история ваших платежей"
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left font-medium text-gray-500 px-6 py-3">Дата</th>
            <th className="text-left font-medium text-gray-500 px-6 py-3">Сумма</th>
            <th className="text-left font-medium text-gray-500 px-6 py-3">Статус</th>
            <th className="text-left font-medium text-gray-500 px-6 py-3">Описание</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((payment) => (
            <tr key={payment.id} className="border-b border-gray-50 last:border-0">
              <td className="px-6 py-3.5 text-gray-700 whitespace-nowrap">
                {formatDateTime(payment.paid_at ?? payment.created_at)}
              </td>
              <td className="px-6 py-3.5 font-medium text-gray-900 whitespace-nowrap">
                {formatRubles(payment.amount_kopecks)}
              </td>
              <td className="px-6 py-3.5">
                <span
                  className={cn(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                    paymentStatusColors[payment.status],
                  )}
                >
                  {PaymentStatusLabels[payment.status]}
                </span>
              </td>
              <td className="px-6 py-3.5 text-gray-500">
                {payment.description ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Loading Skeleton ───────────────────────────────────────

const BillingSkeleton = () => (
  <div className="space-y-6 animate-fadeIn">
    <div>
      <div className="h-7 w-28 bg-gray-200 rounded-md animate-pulse" />
      <div className="h-4 w-56 bg-gray-100 rounded-md animate-pulse mt-2" />
    </div>
    <Card>
      <Card.Body>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-200 animate-pulse" />
            <div className="space-y-2 flex-1">
              <div className="h-5 w-32 bg-gray-200 rounded-md animate-pulse" />
              <div className="h-3.5 w-20 bg-gray-100 rounded-md animate-pulse" />
            </div>
          </div>
          <div className="h-24 bg-gray-100 rounded-xl animate-pulse" />
          <div className="h-12 bg-gray-200 rounded-xl animate-pulse" />
        </div>
      </Card.Body>
    </Card>
    <Card>
      <Card.Body>
        <div className="space-y-3">
          <div className="h-5 w-40 bg-gray-200 rounded-md animate-pulse" />
          <div className="h-10 bg-gray-100 rounded-md animate-pulse" />
          <div className="h-10 bg-gray-100 rounded-md animate-pulse" />
          <div className="h-10 bg-gray-100 rounded-md animate-pulse" />
        </div>
      </Card.Body>
    </Card>
  </div>
);

// ── Main Page ──────────────────────────────────────────────

const BillingPage = () => {
  const {
    data: subscription,
    isLoading: subLoading,
    error: subError,
    refetch: refetchSub,
  } = useSubscriptionStatus();

  const {
    data: paymentsData,
    isLoading: paymentsLoading,
    error: paymentsError,
    refetch: refetchPayments,
  } = usePaymentHistory();

  if (subLoading || paymentsLoading) {
    return <BillingSkeleton />;
  }

  if (subError) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Биллинг</h1>
          <p className="text-sm text-gray-500 mt-1">Управление подпиской и платежами</p>
        </div>
        <ErrorMessage message="Ошибка загрузки" onRetry={() => refetchSub()} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Биллинг</h1>
        <p className="text-sm text-gray-500 mt-1">Управление подпиской и платежами</p>
      </div>

      {subscription && <SubscriptionCard sub={subscription} />}

      <Card>
        <Card.Header>
          <h2 className="font-semibold text-gray-900">История платежей</h2>
        </Card.Header>

        {paymentsError ? (
          <Card.Body>
            <ErrorMessage message="Ошибка загрузки" onRetry={() => refetchPayments()} />
          </Card.Body>
        ) : (
          <Card.Body className="p-0">
            <PaymentHistoryTable payments={paymentsData?.items ?? []} />
          </Card.Body>
        )}
      </Card>
    </div>
  );
};

export default BillingPage;
